import Organization from "@/domain/entities/organization/Organization";
import OrganizationLicense from "@/domain/entities/organization/OrganizationLicense";
import OrganizationUser from "@/domain/entities/organization/OrganizationUser";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { queryKeys } from "@/lib/queryKeys";
import {
    organizationMapper,
    organizationUserMapper,
} from "@/mappers/organizationMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import type {
    LicenseMutationSnapshot,
    OrganizationResponse,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type AffectLicenseParams = {
    licenseId: string;
    user: {
        userId: string;
        userMail: string;
    };
};

type RemoveLicenseParams = {
    userId: string;
    userMail: string;
    orgLicenseId: string;
};

type SetAdminParams = {
    isAdmin: boolean;
    userMail: string;
    userId: string;
};

type UseOrganizationOptions = {
    fetchUsers?: boolean;
};

// Cache state captured before an optimistic update, used to roll back on error.
type OrganizationCacheContext = {
    previousOrganization?: Organization;
    previousOrganizationUsers?: OrganizationUser[];
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const useOrganization = ({
    fetchUsers = false,
}: UseOrganizationOptions = {}) => {
    const { licenseManager } = useSdk();
    const queryClient = useQueryClient();
    const { data: currentUser } = useGetUserProfile();
    const teamId = currentUser?.teamId ?? undefined;
    const organizationUsersQueryKey = queryKeys.organization.users(teamId);

    // Maps an SDK OrganizationResponse to the domain Organization entity, resolving each
    // license's human-readable name. Shared by the getOrganization query and the
    // post-mutation setQueryData (snapshot) so the cache shape stays identical.
    const mapAndEnrichOrganization = useCallback(
        (organizationData: OrganizationResponse): Organization => {
            const organization = organizationMapper(organizationData);

            const enrichedLicenses = organization.licenses.map((license) => {
                let resolvedName = license.licenseId;
                try {
                    const licenseDetail = licenseManager.getLicenseDetailFromId(
                        license.licenseId,
                    );
                    resolvedName = licenseDetail.name || license.licenseId;
                } catch {
                    /* detail not yet available, fallback to licenseId */
                }

                return new OrganizationLicense({
                    id: license.id,
                    name: resolvedName,
                    licenseId: license.licenseId,
                    licenseCount: license.licenseCount,
                    billingType: license.billingType,
                    users: license.users,
                });
            });

            return new Organization({
                id: organization.id,
                name: organization.name,
                adminEmails: organization.adminEmails,
                licenses: enrichedLicenses,
            });
        },
        [licenseManager],
    );

    const getOrganization = useCallback(async (): Promise<Organization> => {
        const organizationData = await licenseManager.getTeamLicenseData();

        return mapAndEnrichOrganization(organizationData);
    }, [licenseManager, mapAndEnrichOrganization]);

    const {
        data: organization,
        isLoading: isLoadingOrganization,
        isError: isOrganizationError,
    } = useQuery({
        queryKey: queryKeys.organization.all(),
        queryFn: getOrganization,
    });

    const isUserAdmin = useCallback(
        (userMail: string) =>
            organization?.adminEmails
                ?.map(normalizeEmail)
                .includes(normalizeEmail(userMail)) ?? false,
        [organization],
    );

    const adminCount = organization?.adminEmails?.length ?? 0;
    const currentUserIsAdmin = isUserAdmin(currentUser?.profile.email ?? "");

    const getOrganizationUsers = useCallback(async (): Promise<
        OrganizationUser[]
    > => {
        if (!teamId) {
            return [];
        }

        const organizationUsers =
            await licenseManager.getAllUsersFromTenant(teamId);

        return organizationUsers.map(organizationUserMapper);
    }, [teamId, licenseManager]);

    const {
        data: organizationUsers,
        isLoading: isLoadingOrganizationUsers,
        isError: isOrganizationUsersError,
    } = useQuery({
        queryKey: organizationUsersQueryKey,
        queryFn: getOrganizationUsers,
        enabled: fetchUsers && !!teamId && currentUserIsAdmin,
        staleTime: 10 * 60 * 1000,
    });

    // Cancel in-flight org/users queries so a background refetch cannot clobber an
    // optimistic update mid-mutation.
    const cancelOrganizationQueries = useCallback(async () => {
        await queryClient.cancelQueries({
            queryKey: queryKeys.organization.all(),
        });
        await queryClient.cancelQueries({
            queryKey: queryKeys.organization.usersAll(),
        });
    }, [queryClient]);

    const captureCaches = useCallback(
        (): OrganizationCacheContext => ({
            previousOrganization: queryClient.getQueryData<Organization>(
                queryKeys.organization.all(),
            ),
            previousOrganizationUsers: queryClient.getQueryData<
                OrganizationUser[]
            >(organizationUsersQueryKey),
        }),
        [queryClient, organizationUsersQueryKey],
    );

    // Roll back to the pre-mutation cache. No refetch: the success path reconciles via
    // the SDK snapshot, so on error we only need to restore what the optimistic update changed.
    const rollbackCaches = useCallback(
        (context?: OrganizationCacheContext) => {
            if (!context) {
                return;
            }
            if (context.previousOrganization !== undefined) {
                queryClient.setQueryData(
                    queryKeys.organization.all(),
                    context.previousOrganization,
                );
            }
            if (context.previousOrganizationUsers !== undefined) {
                queryClient.setQueryData(
                    organizationUsersQueryKey,
                    context.previousOrganizationUsers,
                );
            }
        },
        [queryClient, organizationUsersQueryKey],
    );

    // Safety net: refetch org + users from the server. Used as a fallback when a successful
    // mutation does not carry the expected snapshot (e.g. an endpoint not yet aligned to
    // return it), so a server-side success never turns into a hard client crash.
    const invalidateOrganizationCaches = useCallback(
        () =>
            Promise.all([
                queryClient.invalidateQueries({
                    queryKey: queryKeys.organization.all(),
                }),
                queryClient.invalidateQueries({
                    queryKey: queryKeys.organization.usersAll(),
                }),
            ]),
        [queryClient],
    );

    // Patch a single user row in the users cache, matched by userId (the stable key the
    // table already uses as OrganizationUser.id).
    const patchOrganizationUser = useCallback(
        (
            userId: string,
            updater: (user: OrganizationUser) => OrganizationUser,
        ) => {
            queryClient.setQueryData<OrganizationUser[]>(
                organizationUsersQueryKey,
                (previousUsers) =>
                    previousUsers?.map((organizationUser) =>
                        organizationUser.id === userId
                            ? updater(organizationUser)
                            : organizationUser,
                    ),
            );
        },
        [queryClient, organizationUsersQueryKey],
    );

    // Authoritative reconcile from the SDK post-mutation snapshot: updated organization
    // + the single impacted user row (matched by userId). Single source of truth, no refetch.
    const applyLicenseMutationSnapshot = useCallback(
        (snapshot: LicenseMutationSnapshot, userId: string) => {
            // Resilience: the mutation succeeded server-side but the snapshot is missing
            // (e.g. an endpoint not yet aligned to return it). Fall back to a refetch
            // instead of crashing on mapAndEnrichOrganization(undefined).
            if (!snapshot.organization || !snapshot.affectedUser) {
                return invalidateOrganizationCaches();
            }

            queryClient.setQueryData(
                queryKeys.organization.all(),
                mapAndEnrichOrganization(snapshot.organization),
            );

            patchOrganizationUser(
                userId,
                (organizationUser) =>
                    new OrganizationUser({
                        id: organizationUser.id,
                        displayName: organizationUser.displayName,
                        mail: organizationUser.mail,
                        isMonthlyAnalyzedMailLimitReached:
                            snapshot.affectedUser
                                .isMonthlyAnalyzedMailLimitReached,
                        license: snapshot.affectedUser.activeLicense
                            ? {
                                  id: snapshot.affectedUser.activeLicense.id,
                                  licenseId:
                                      snapshot.affectedUser.activeLicense
                                          .licenseId,
                              }
                            : undefined,
                    }),
            );

            return undefined;
        },
        [
            queryClient,
            mapAndEnrichOrganization,
            patchOrganizationUser,
            invalidateOrganizationCaches,
        ],
    );

    const affectTeamLicenseToUser = useCallback(
        async ({ licenseId, user }: AffectLicenseParams) => {
            return licenseManager.affectTeamLicenseToUser(licenseId, user);
        },
        [licenseManager],
    );

    const { mutateAsync: affectLicense } = useMutation({
        mutationFn: affectTeamLicenseToUser,
        // Optimistic: show the target license on the row immediately.
        onMutate: async ({ licenseId: targetSlotId, user }) => {
            await cancelOrganizationQueries();

            const context = captureCaches();
            const targetSlot = context.previousOrganization?.licenses.find(
                (license) => license.id === targetSlotId,
            );

            if (targetSlot) {
                patchOrganizationUser(
                    user.userId,
                    (organizationUser) =>
                        new OrganizationUser({
                            id: organizationUser.id,
                            displayName: organizationUser.displayName,
                            mail: organizationUser.mail,
                            isMonthlyAnalyzedMailLimitReached:
                                organizationUser.isMonthlyAnalyzedMailLimitReached,
                            license: {
                                id: targetSlotId,
                                licenseId: targetSlot.licenseId,
                            },
                        }),
                );
            }

            return context;
        },
        // Reconcile with the authoritative snapshot (falls back to a refetch if missing).
        onSuccess: (snapshot, { user }) =>
            applyLicenseMutationSnapshot(snapshot, user.userId),
        onError: (_error, _variables, context) => {
            rollbackCaches(context);
        },
    });

    const removeTeamLicenseFromUser = useCallback(
        async (params: RemoveLicenseParams) => {
            return licenseManager.removeTeamLicenseFromUser(params);
        },
        [licenseManager],
    );

    const { mutateAsync: removeLicense } = useMutation({
        mutationFn: removeTeamLicenseFromUser,
        // Optimistic: clear the row's license immediately.
        onMutate: async ({ userId }) => {
            await cancelOrganizationQueries();

            const context = captureCaches();

            patchOrganizationUser(
                userId,
                (organizationUser) =>
                    new OrganizationUser({
                        id: organizationUser.id,
                        displayName: organizationUser.displayName,
                        mail: organizationUser.mail,
                        isMonthlyAnalyzedMailLimitReached:
                            organizationUser.isMonthlyAnalyzedMailLimitReached,
                        license: undefined,
                    }),
            );

            return context;
        },
        onSuccess: (snapshot, { userId }) =>
            applyLicenseMutationSnapshot(snapshot, userId),
        onError: (_error, _variables, context) => {
            rollbackCaches(context);
        },
    });

    // Toggle a user's admin membership in the cached organization (the Admin column derives
    // from organization.adminEmails). Idempotent — used both for the optimistic update and
    // for the reconcile from the server's authoritative { userMail, isAdmin }.
    const applyAdminToggleToCache = useCallback(
        (userMail: string, isAdmin: boolean) => {
            queryClient.setQueryData<Organization>(
                queryKeys.organization.all(),
                (previousOrganization) => {
                    if (!previousOrganization) {
                        return previousOrganization;
                    }

                    const adminEmails = previousOrganization.adminEmails ?? [];
                    const normalizedUserMail = normalizeEmail(userMail);
                    const hasAdminEmail = adminEmails.some(
                        (adminEmail) =>
                            normalizeEmail(adminEmail) === normalizedUserMail,
                    );

                    let nextAdminEmails: string[];
                    if (!isAdmin) {
                        nextAdminEmails = adminEmails.filter(
                            (adminEmail) =>
                                normalizeEmail(adminEmail) !==
                                normalizedUserMail,
                        );
                    } else if (hasAdminEmail) {
                        nextAdminEmails = adminEmails;
                    } else {
                        nextAdminEmails = [...adminEmails, userMail];
                    }

                    return new Organization({
                        id: previousOrganization.id,
                        name: previousOrganization.name,
                        adminEmails: nextAdminEmails,
                        licenses: previousOrganization.licenses,
                    });
                },
            );
        },
        [queryClient],
    );

    const setUserTeamAdmin = useCallback(
        async ({ isAdmin, userId, userMail }: SetAdminParams) => {
            return licenseManager.setUserTeamAdmin(userId, userMail, isAdmin);
        },
        [licenseManager],
    );

    const { mutateAsync: setAdmin } = useMutation({
        mutationFn: setUserTeamAdmin,
        // Optimistic: toggle the admin email list immediately (the Admin column derives
        // from organization.adminEmails).
        onMutate: async ({ userMail, isAdmin }) => {
            await cancelOrganizationQueries();

            const context = captureCaches();
            applyAdminToggleToCache(userMail, isAdmin);

            return context;
        },
        // Reconcile from the server's authoritative { userMail, isAdmin }. This endpoint does
        // not return an organization snapshot, so we re-apply the toggle (idempotent), no refetch.
        onSuccess: ({ userMail, isAdmin }) => {
            applyAdminToggleToCache(userMail, isAdmin);
        },
        onError: (_error, _variables, context) => {
            rollbackCaches(context);
        },
    });

    return {
        adminCount,
        affectLicense,
        currentUserIsAdmin,
        isError: isOrganizationError || isOrganizationUsersError,
        isLoading: isLoadingOrganization || isLoadingOrganizationUsers,
        isUserAdmin,
        organization,
        organizationUsers,
        removeLicense,
        setAdmin,
    };
};

export default useOrganization;
